<template>
  <span>
    <b-button @click="show">Import</b-button>
    <b-modal
      title="Import deck"
      v-model="showModal"
      size="xl"
      :ok-disabled="!isValid"
      @ok="outputDeck"
    >
      <b-tabs v-model="isJSON" @input="reset" content-class="m-3">
        <b-tab title="JSON">
          <b-form-textarea rows="15" :state="isValid" @input="parseJSONDeck">
          </b-form-textarea>
        </b-tab>
        <b-tab title="Spreadsheet">
          <p>
            For importing decks from the
            <a
              href="https://docs.google.com/spreadsheets/d/1qt_d1ZvIrPbDM4AcIuSCYywXaKDW-FqrMH4wnU-vqNQ/copy?usp=sharing"
              target="_blank"
              >Google Sheet</a
            >
            for the
            <a
              href="https://restorationgames.com/unmatched/unmatched-design-contest/"
              target="_blank"
              >Fan Deck Contest</a
            >.
          </p>
          <em>
            Please note:
            <ul>
              <li>The deck name is not imported</li>
              <li>
                All card text is imported as "basic" text and not assigned to
                the <em>Immediate/During/After</em> slots.
              </li>
            </ul>
          </em>
          <b-form-group
            label="Hero Power"
            description="Paste the entire 'Hero Power' row for one character here."
          >
            <b-form-textarea
              rows="4"
              :state="isCharacterValid"
              @input="parseTSVHeroPower"
            >
            </b-form-textarea>
          </b-form-group>
          <b-form-group
            label="Card List"
            description="Paste all 'Card List' rows for one deck here."
          >
            <b-form-textarea
              rows="15"
              :state="isDeckValid"
              @input="parseTSVCardList"
            >
            </b-form-textarea>
          </b-form-group>
        </b-tab>
        <b-tab title="HUmN">
          <b-form-textarea rows="15" :state="isValid" @input="parseHumanDeck">
          </b-form-textarea>
        </b-tab>
      </b-tabs>
    </b-modal>
  </span>
</template>

<script>
const nearley = require("nearley");
import grammar from "@/parser/unmatchedParser.js";

export default {
  name: "ImportDeck",
  data() {
    return {
      deck: null,
      isValid: null,
      isJSON: 0,
      showModal: false,
      isCharacterValid: null,
      isDeckValid: null
    };
  },
  methods: {
    show() {
      this.showModal = true;
      this.reset();
    },
    reset() {
      this.deck = null;
      this.isValid = null;
      this.isCharacterValid = null;
      this.isDeckValid = null;
    },
    outputDeck() {
      const sidekickDefaults = {
        name: "Sidekick",
        isRanged: true,
        hp: 1,
        quantity: 1,
        quote: ""
      };
      this.deck.sidekick = { ...sidekickDefaults, ...this.deck.sidekick };
      this.$emit("input", this.deck);
    },
    parseTSVCardList(value) {
      if (!value) {
        this.isValid = null;
        this.isDeckValid = null;
        return;
      }
      try {
        if (this.deck === null) {
          this.deck = {};
        }
        const cards = value.trim().match(/[^\r\n]+/g);
        this.deck.cards = [];
        cards.forEach(card => {
          card = card.trim().split("\t");
          const cardTitle = card[2].trim();
          const existingCard = this.deck.cards.find(
            ec => ec.title === cardTitle
          );
          if (existingCard) {
            existingCard.quantity++;
            return;
          }
          const power = card[4].trim();
          const attack = card[5].trim();
          const defence = card[6].trim();
          const cardType = power
            ? "versatile"
            : attack
            ? "attack"
            : defence
            ? "defence"
            : "scheme";
          const cardValue = parseInt(power || attack || defence || 0);
          this.deck.cards.push({
            characterName: card[1],
            title: cardTitle,
            boost: parseInt(card[3]),
            value: cardValue,
            type: cardType,
            afterText: "",
            duringText: "",
            immediateText: "",
            basicText: card[8].trim(),
            imageUrl: "",
            quantity: 1
          });
        });
        this.isDeckValid = true;
      } catch {
        this.isDeckValid = false;
      }
      this.isValid = this.isCharacterValid && this.isDeckValid;
    },
    parseTSVHeroPower(value) {
      if (!value) {
        this.isValid = null;
        this.isCharacterValid = null;
        return;
      }
      try {
        if (this.deck === null) {
          this.deck = {};
        }
        const splitInput = value.trim().split("\t");
        this.deck.hero = {
          name: splitInput[1],
          move: parseInt(splitInput[2]),
          hp: parseInt(splitInput[3]),
          isRanged: splitInput[4].trim().toLocaleLowerCase() === "ranged",
          specialAbility: splitInput[5]
        };
        this.deck.sidekick = {
          name: splitInput[6],
          hp: parseInt(splitInput[7]),
          quantity: parseInt(splitInput[8]),
          isRanged: splitInput[9].trim().toLocaleLowerCase() === "ranged"
        };
        this.isCharacterValid = true;
      } catch {
        this.isCharacterValid = false;
      }
      this.isValid = this.isCharacterValid && this.isDeckValid;
    },
    parseJSONDeck(value) {
      if (!value) {
        this.isValid = null;
        return;
      }
      try {
        const deck = JSON.parse(value);
        this.isValid = true;
        this.deck = deck;
      } catch {
        this.isValid = false;
      }
    },
    parseHumanDeck(value) {
      if (!value) {
        this.isValid = null;
        return;
      }
      const parser = new nearley.Parser(nearley.Grammar.fromCompiled(grammar));
      var deck = {};
      try {
        parser.feed(value);
        const parsedDeck = parser.results[0];
        try {
          const appearance = JSON.parse(parsedDeck.metadata);
          if (appearance) {
            Object.assign(deck, appearance);
          }
        } catch (err) {
          this.isValid = false;
          return;
        }
        if (parsedDeck.hero) {
          if (!deck.hero) {
            deck.hero = {};
          }
          Object.assign(deck.hero, parsedDeck.hero);
        }
        if (parsedDeck.sidekick) {
          if (!deck.sidekick) {
            deck.sidekick = {};
          }
          if (!("quantity" in parsedDeck.sidekick)) {
            deck.sidekick.quantity = 1;
          }
          Object.assign(deck.sidekick, parsedDeck.sidekick);
        }
        if (parsedDeck.cards) {
          deck.cards = [];
          parsedDeck.cards.forEach((card, index) => {
            this.$set(deck.cards, index, card);
          });
        }
        this.isValid = true;
        this.deck = deck;
      } catch (err) {
        this.isValid = false;
      }
    }
  }
};
</script>

<style lang="less" scoped>
textarea {
  width: 100%;
  font-family: monospace;
}
</style>
